import React, { useState } from 'react';

interface RegistrationFormProps {
  onSubmit: (formData: {
    name: string;
    email: string;
    phone?: string;
    join2025: boolean;
    membershipType: 'student' | 'single' | 'family' | null;
    isVietnamese: boolean;
    isSenior: boolean;
    notes: string;
  }) => Promise<void>;
  onCancel?: () => void;
  initialName?: string;
}

export function RegistrationForm({ onSubmit, onCancel, initialName = '' }: RegistrationFormProps) {
  const [formData, setFormData] = useState({
    name: initialName,
    email: '',
    phone: '',
    join2025: false,
    membershipType: null,
    isVietnamese: false,
    isSenior: false,
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const validateEmail = (email: string) => {
    if (!email.trim()) return 'Email is required';
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email.trim())) {
      return 'Invalid email format';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    const emailValidation = validateEmail(formData.email);
    if (emailValidation) {
      setEmailError(emailValidation);
      setIsSubmitting(false);
      return;
    }
    
    try {
      await onSubmit(formData);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="max-h-[80vh] overflow-y-auto">
      <h2 className="text-xl font-semibold mb-4">New Member Registration</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Full Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            required
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={(e) => {
              handleChange(e);
              setEmailError(validateEmail(e.target.value));
            }}
            className={`mt-1 block w-full rounded-md shadow-sm ${
              emailError ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
            }`}
            required
          />
          {emailError && (
            <p className="mt-1 text-sm text-red-600">{emailError}</p>
          )}
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
            Phone Number
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              id="join2025"
              name="join2025"
              type="checkbox"
              checked={formData.join2025}
              onChange={handleChange}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="join2025" className="font-medium text-gray-700">
              Join 2025 Membership
            </label>
            <p className="text-gray-500">
              Sign up for the 2025 season (Nov '24 - May '25)
            </p>
          </div>
        </div>

        {formData.join2025 && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Membership Type
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="membershipType"
                  value="student"
                  checked={formData.membershipType === 'student'}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    membershipType: e.target.checked ? 'student' : null
                  }))}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Student Membership - $100</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="membershipType"
                  value="single"
                  checked={formData.membershipType === 'single'}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    membershipType: e.target.checked ? 'single' : null
                  }))}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Single Membership - $150</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="membershipType"
                  value="family"
                  checked={formData.membershipType === 'family'}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    membershipType: e.target.checked ? 'family' : null
                  }))}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                />
                <span className="ml-2 text-sm text-gray-700">Family Membership - $250</span>
              </label>
            </div>
          </div>
        )}

        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              id="isVietnamese"
              name="isVietnamese"
              type="checkbox"
              checked={formData.isVietnamese}
              onChange={handleChange}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="isVietnamese" className="font-medium text-gray-700">
              Vietnamese Heritage
            </label>
            <p className="text-gray-500">
              Check if you have Vietnamese heritage
            </p>
          </div>
        </div>

        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              id="isSenior"
              name="isSenior"
              type="checkbox"
              checked={formData.isSenior}
              onChange={handleChange}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="isSenior" className="font-medium text-gray-700">
              Age - 55 and above
            </label>
            <p className="text-gray-500">
              Check if you are 55 years or older
            </p>
          </div>
        </div>

        {/* Notes section commented out
          <div className="mb-6">
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              maxLength={1000}
              rows={4}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm resize-y"
              placeholder="Enter any notes..."
            />
            <p className="mt-1 text-xs text-gray-500">
              {formData.notes.length}/1000 characters
            </p>
          </div>
        */}
        
        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}

        <div className="flex justify-end gap-3">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            {isSubmitting ? 'Registering...' : 'Register'}
          </button>
        </div>
      </form>
    </div>
  );
}